import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
	Typography,
	CircularProgress,
	TextField,
	Button,
	Box,
	Paper,
} from "@mui/material";
import { MdDeliveryDining, MdOutlineCancel } from "react-icons/md";
import { FaNetworkWired } from "react-icons/fa";
import { decode as base64Decode } from "js-base64";

// ===================================
// BASE64 DECODER
// ===================================
const isBase64 = (str) => {
	try {
		return btoa(atob(str)) === str;
	} catch {
		return false;
	}
};

const decodeText = (text) => (isBase64(text) ? base64Decode(text) : text);

// ===================================
// ERROR CODE DESCRIPTIONS
// ===================================
const errorCodeDescriptions = {
	"000": "Delivered",
	"0dc": "Absent Subscriber",
	206: "Absent Subscriber",
	"21b": "Absent Subscriber",
	"023": "Absent Subscriber",
	"027": "Absent Subscriber",
	"053": "Absent Subscriber",
	"054": "Absent Subscriber",
	"058": "Absent Subscriber",
	439: "Absent subscriber or ported subscriber or subscriber is barred",
	254: "Subscriber's phone inbox is full",
	220: "Subscriber's phone inbox is full",
	120: "Subscriber's phone inbox is full",
	"008": "Subscriber's phone inbox is full",
	255: "Invalid or inactive mobile number or subscriber's phone inbox is full",
	"20b": "Invalid or inactive mobile number",
	"004": "Invalid or inactive mobile number",
	510: "Invalid or inactive mobile number",
	215: "Invalid or inactive mobile number",
	"20d": "Subscriber is barred on the network",
	130: "Subscriber is barred on the network",
	131: "Subscriber is barred on the network",
	222: "Network operator system failure",
	602: "Network operator system failure",
	306: "Network operator system failure",
	"032": "Network operator system failure or operator not supported",
	"085": "Subscriber is on DND",
	"065": "Message content or senderID is blocked on the promotional route",
	600: "Message content or senderID is blocked on the promotional route",
	"40a": "SenderID not whitelisted on the account",
	"082": "Network operator not supported",
	"00a": "SenderID is restricted by the operator",
	"078": "Restricted message content or senderID is blocked.",
	432: "Restricted message content or senderID is blocked.",
};

// ===================================
// STATUS COMPONENT
// ===================================
const getStatusComponent = (status) => {
	const commonClass =
		"px-2 py-1 rounded-md text-sm flex items-center gap-1";

	switch (status) {
		case "":
			return (
				<p className={`${commonClass} bg-yellow-100 text-yellow-800`}>
					<FaNetworkWired /> Pending
				</p>
			);

		case "DELIVRD":
			return (
				<p className={`${commonClass} bg-green-100 text-green-800`}>
					<MdDeliveryDining /> Delivered
				</p>
			);

		case "EXPIRED":
			return (
				<p className={`${commonClass} bg-gray-200 text-gray-800`}>
					<FaNetworkWired /> Expired
				</p>
			);

		case "UNDELIV":
		case "0":
			return (
				<p className={`${commonClass} bg-red-100 text-red-600`}>
					<FaNetworkWired /> Undelivered
				</p>
			);

		case "REJECTD":
			return (
				<p className={`${commonClass} bg-red-200 text-red-800`}>
					<MdOutlineCancel /> Rejected
				</p>
			);

		default:
			return (
				<p className={`${commonClass} bg-blue-100 text-blue-800`}>
					<FaNetworkWired /> {status || "Sent"}
				</p>
			);
	}
};

// ===================================
// INTERNATIONAL MESSAGES
// ===================================
const InternationalMessages = () => {
	const [messages, setMessages] = useState([]);
	const [loading, setLoading] = useState(true);
	const [filterValue, setFilterValue] = useState("");
	const token = sessionStorage.getItem("token");

	// ===================================
	// FETCH INTERNATIONAL MESSAGES
	// ===================================
	const fetchMessages = async () => {
		setLoading(true);

		try {
			const { data } = await axios.get(
				"https://ecobank.approot.ng/international.php",
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (data?.status === "success") {
				setMessages(data.data || []);
			} else {
				setMessages([]);
				console.error("International messages error:", data);
			}
		} catch (error) {
			console.error(
				"Error fetching international messages:",
				error
			);
			setMessages([]);
		} finally {
			setLoading(false);
		}
	};

	// Load messages when page opens
	useEffect(() => {
		fetchMessages();
	}, []);

	// ===================================
	// SEARCH INTERNATIONAL MESSAGES
	// ===================================
	const filterTable = async () => {
		const searchValue = filterValue.trim();

		// If search box is empty, reload normal list
		if (!searchValue) {
			fetchMessages();
			return;
		}

		setLoading(true);

		try {
			const { data } = await axios.get(
				`https://ecobank.approot.ng/international.php?phone=${encodeURIComponent(
					searchValue
				)}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (data?.status === "success") {
				setMessages(data.data || []);
			} else {
				setMessages([]);
				console.error("International search error:", data);
			}
		} catch (error) {
			console.error(
				"Error searching international messages:",
				error
			);
			setMessages([]);
		} finally {
			setLoading(false);
		}
	};

	// ===================================
	// TABLE COLUMNS
	// ===================================
	const columns = [
		{
			field: "senderid",
			headerName: "Sender ID",
			flex: 1.2,
			minWidth: 180,
		},
		{
			field: "msisdn",
			headerName: "Recipient",
			flex: 1,
			minWidth: 180,
			valueGetter: (params) => {
    const value = String(params ?? "").trim();
    return value.startsWith("+") ? value : `+${value}`;
},
		},
		{
			field: "text",
			headerName: "Message Content",
			flex: 2,
			minWidth: 180,
			renderCell: (params) => {
				const decodedText = decodeText(params?.row?.text);

				return (
					<Box
						sx={{
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
							fontSize: "0.875rem",
							color: "#333",
							width: "100%",
						}}
						title={decodedText}
					>
						{decodedText}
					</Box>
				);
			},
		},
		{
			field: "dlr_status",
			headerName: "Status",
			minWidth: 180,
			flex: 1.2,
			renderCell: (params) =>
				getStatusComponent(params?.row?.dlr_status),
		},
		{
			field: "network",
			headerName: "Network",
			flex: 1,
			minWidth: 120,
		},
		{
			field: "dlr_request",
			headerName: "Error Code & Reason",
			flex: 2,
			minWidth: 180,
			renderCell: (params) => {
				let code = params.value;
				const description = errorCodeDescriptions[code];

				let finalDescription;

				if (description) {
					finalDescription = description;
				} else {
					code = code || "21b";
					finalDescription = "Unknown";
				}

				return (
					<Box
						display='flex'
						flexDirection='column'
						fontSize='0.875rem'
					>
						<strong>
							{code}{" "}
							<small style={{ color: "#666" }}>
								{finalDescription}
							</small>
						</strong>
					</Box>
				);
			},
		},
		{
			field: "created_at",
			headerName: "Sent At",
			flex: 1.5,
			minWidth: 200,
		},
		{
			field: "updated_at",
			headerName: "DLR Received At",
			flex: 1.5,
			minWidth: 200,
		},
	];

	const rows = messages.map((msg, index) => ({
		id: msg.id || index,
		...msg,
	}));

	// ===================================
	// LOADING
	// ===================================
	if (loading) {
		return (
			<Box
				display='flex'
				justifyContent='center'
				alignItems='center'
				height='60vh'
			>
				<CircularProgress />
			</Box>
		);
	}

	// ===================================
	// RENDER
	// ===================================
	return (
		<Box
			px={6}
			py={5}
			bgcolor='#f9f9f9'
			minHeight='100vh'
		>
			<Typography
				variant='h5'
				fontWeight={600}
				mb={4}
				color='text.primary'
			>
				International SMS Message Logs
			</Typography>

			<Box
				display='flex'
				gap={2}
				alignItems='center'
				mb={4}
				component={Paper}
				elevation={1}
				p={3}
				borderRadius={2}
			>
				<TextField
					label='Search by phone number'
					variant='outlined'
					size='small'
					fullWidth
					value={filterValue}
					onChange={(e) =>
						setFilterValue(e.target.value)
					}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							filterTable();
						}
					}}
				/>

				<Button
					variant='contained'
					color='primary'
					onClick={filterTable}
					sx={{ minWidth: 120 }}
				>
					Search
				</Button>
			</Box>

			<Paper
				elevation={2}
				sx={{
					height: 650,
					borderRadius: 2,
				}}
			>
				<DataGrid
					rows={rows}
					columns={columns}
					pageSize={10}
					rowsPerPageOptions={[10, 20, 50]}
					disableColumnMenu
					disableSelectionOnClick
					sx={{
						border: "none",
						fontSize: "0.875rem",
						"& .MuiDataGrid-columnHeaders": {
							backgroundColor: "#f3f4f6",
							fontWeight: "bold",
						},
						"& .MuiDataGrid-cell": {
							whiteSpace: "normal",
							wordBreak: "break-word",
							lineHeight: "1.4",
						},
					}}
					showToolbar
					slotProps={{
						toolbar: {
							csvOptions: {
								fileName:
									"ecobank_international_sms_report",
								delimiter: ";",
								utf8WithBom: true,
							},
						},
					}}
				/>
			</Paper>
		</Box>
	);
};

export default InternationalMessages;